
export interface ActionItem {
  transferType: { _text: string }
  confirm?: { _text: string }
  'event.chainCalled'?: any
}

/**
 * Композабл для работы с действиями платформы
 */
export function useActions() {
  const needToCallDoAction = (actionItem: ActionItem): boolean => {
    // Normalize to string to handle both string and number transfer types
    const transferType = String(actionItem.transferType._text).toLowerCase()
    const doActionTypes = [
      // numeric TransferType codes (server / JSON)
      '101',
      '102',
      '103',
      '105',
      '108',
      '120',
      '121',
      '130',
      '10301',
      '10302',
      '10303',
      '10304',
      '10305',
      '10801',
      '10802',
      '10803',
      '10804',
      '10805',
      '1080501',
      '10806',
      'call_chain',
      'call_chain_and_explicit_print',
      'explicit_print',
      'print_document',
      'call_chain_and_return',
      'call_chain_conditional_return',
      'call_chain_change_doc',
      'call_chain_conditional_change_doc',
      'context_call_conditional_change_doc',
      'document',
      'represent_user_document',
      'context_call_change_doc',
      'context_call_and_return',
      'context_call_and_explicit_view',
      'context_call_and_conditional_explicit_view',
      'call_chain_and_context_call',
      'call_chain_and_context_call_and_return',
      'context_call',
      'returning',
      'call_chain_and_explicit_view',
      'explicit_view',
      // Numeric return transfer types (for step navigation back)
      // 5 = TT_RETURN_NONE
      // 6 = TT_RETURN_REPLACE
      // 7 = TT_RETURN_PREV_NONE
      // 8 = TT_RETURN_PREV_REPLACE
      // 9 = TT_RETURN_PREV_CUR_DATA_NONE
      // 10 = TT_RETURN_PREV_CUR_DATA_REPLACE
      // 11 = TT_RETURN_TO_LAST_FORM
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '11',
      // Enum name return transfer types (alternative format from server)
      'return_none',
      'return_replace',
      'return_prev_none',
      'return_prev_replace',
      'return_prev_cur_data_none',
      'return_prev_cur_data_replace',
      'return_to_last_form',
    ]
    return doActionTypes.includes(transferType)
  }

  /**
   * Определяет, нужен ли вызов execute-procedure
   */
  const needToExecuteProcedure = (actionItem: ActionItem): boolean => {
    const transferType = String(actionItem.transferType?._text ?? '').trim()
    const isContextRaw = (actionItem as { isContext?: { _text?: string } | string }).isContext
    const isContext = String(
      typeof isContextRaw === 'object' && isContextRaw != null
        ? (isContextRaw._text ?? '')
        : (isContextRaw ?? ''),
    )
      .trim()
      .toLowerCase()
    if (
      transferType.toLowerCase() === 'call_chain_and_return' &&
      (!isContext || isContext === 'none')
    ) {
      return false
    }
    return [
      'context_call_and_return',
      'context_call_conditional_change_doc',
      'call_chain',
      'call_chain_conditional_change_doc',
      'call_chain_and_return',
      'call_chain_conditional_return',
      'call_chain_and_explicit_view',
    ].includes(transferType)
  }

  /**
   * Определяет, нужно ли выполнить событие chainCalled
   */
  const needToCallChain = (actionItem: ActionItem): boolean => {
    if (!actionItem['event.chainCalled'])
      return false

    return [
      'context',
      'context_call_conditional_change_doc',
      'call_chain',
      'call_chain_conditional_change_doc',
      'call_chain_conditional_return',
    ].includes(actionItem.transferType._text)
  }

  /**
   * Определяет, нужно ли выполнить событие dialogClosed
   * и закрыть диалоговое окно
   */
  const needToCloseModal = (actionItem: ActionItem): boolean => {
    // Normalize to string to handle both string and number transfer types
    const type = String(actionItem.transferType._text).toLowerCase()

    const stepNavigationReturnTypes = [
      '5', '6', '7', '8', '9', '10', '11',
      'return_none', 'return_replace', 'return_prev_none', 'return_prev_replace',
      'return_prev_cur_data_none', 'return_prev_cur_data_replace', 'return_to_last_form'
    ]

    // If it's a step navigation return type, don't close modal
    if (stepNavigationReturnTypes.includes(type)) {
      return false
    }

    // returning as numeric enum code (TransferType.returning = 105)
    if (type === '105') {
      return true
    }

    // For other return types (like 'returning', 'call_chain_and_return'), close modal
    if (type.indexOf('return') > -1) {
      return true
    }

    return false
  }

  return {
    needToCallDoAction,
    needToExecuteProcedure,
    needToCallChain,
    needToCloseModal
  }
}
